'use client'

import { X } from '@phosphor-icons/react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import {
  ComponentPropsWithoutRef,
  ElementRef,
  HTMLAttributes,
  ReactNode,
  TouchEvent,
  forwardRef,
} from 'react'
import { cn } from '@/core/lib/utils'

const isStylusTouch = (event: TouchEvent<HTMLElement>) =>
  Array.from(event.touches).some(
    (touch) => (touch as Touch & { touchType?: string }).touchType === 'stylus'
  )

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const Dialog = (
  props: ComponentPropsWithoutRef<typeof DialogPrimitive.Root>
) => {
  return <DialogPrimitive.Root {...props} />
}

const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    withPositioning?: boolean
    mobileBottom?: boolean
    /// Disables the zoom animation to prevent map glitches
    mapSafe?: boolean
  }
>(
  (
    {
      className,
      children,
      withPositioning = true,
      mobileBottom = true,
      mapSafe = false,
      onTouchMove,
      ...props
    },
    ref
  ) => {
    return (
      <DialogPortal forceMount={props.forceMount}>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50',
            'bg-black/60',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0% data-[state=open]:animate-in data-[state=open]:fade-in-0%'
          )}
        />
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'fixed z-50 flex w-full flex-col bg-white',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0% data-[state=open]:animate-in data-[state=open]:fade-in-0%',
            {
              'data-[state=closed]:zoom-out-95% data-[state=open]:zoom-in-95%':
                !mapSafe,
              'data-[state=closed]:slide-out-to-top-5% data-[state=open]:slide-in-from-top-5%':
                !mobileBottom,
              'data-[state=closed]:slide-out-to-bottom-5% data-[state=open]:slide-in-from-bottom-5% md:data-[state=closed]:slide-out-to-top-5% md:data-[state=open]:slide-in-from-top-5%':
                mobileBottom,
              'bottom-0 max-h-[90%] rounded-t-lg lg:top-16 lg:bottom-auto lg:left-1/2 lg:max-h-[calc(100%-64px-64px)] lg:max-w-3xl lg:-translate-x-1/2 lg:rounded-lg':
                withPositioning && mobileBottom,
              'inset-x-4 top-16 left-1/2 max-h-[calc(100%-144px)] max-w-3xl -translate-x-1/2 rounded-lg':
                withPositioning && !mobileBottom,
            },
            className
          )}
          tabIndex={-1}
          {...props}
          onTouchMove={(event) => {
            onTouchMove?.(event)
            if (!event.isPropagationStopped() && isStylusTouch(event)) {
              event.stopPropagation()
            }
          }}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    )
  }
)
// @ts-ignore
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  title,
  description,
  close = true,
  className,
  children,
  ...props
}: {
  title: string
  description?: ReactNode
  close?: boolean
} & Omit<HTMLAttributes<HTMLDivElement>, 'title'>) => {
  return (
    <div
      className={cn(
        'relative flex justify-between border-b border-gray-150 px-5 pt-5 pb-3 lg:px-8 lg:pt-6',
        className
      )}
      {...props}
    >
      <div>
        <DialogPrimitive.Title className="text-lg font-black lg:text-2xl">
          {title}
        </DialogPrimitive.Title>
        {description && (
          <DialogPrimitive.Description className="inline-flex items-center text-sm text-gray-700">
            {description}
          </DialogPrimitive.Description>
        )}
        {children}
      </div>
      {close && (
        <DialogPrimitive.Close className="self-start disabled:pointer-events-none">
          <X weight="bold" size={24} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

const DialogBody = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={cn(
        'overflow-y-auto px-5 pt-6 pb-6 lg:px-8 lg:py-8',
        className
      )}
      {...props}
    />
  )
}

const DialogSimple = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div className={cn('overflow-y-auto p-5 lg:p-6', className)} {...props} />
  )
}

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogSimple,
}
